import PropTypes from 'prop-types'
import React from 'react'
import Router from 'next/router'
import { connect } from 'react-redux'
import CommonLayout from 'components/layout/common'
import ExchangeRateApiHandler from 'lib/api/exchange'
import ProductContainer from 'components/pdp/ProductContainer'
import productDuck from 'components/product/product-item/duck'
import ProductApiHandler, { API_INVALID_PRODUCT_RES } from 'lib/api/product'
import { withI18next } from 'lib/i18n/withI18next'
import { segmentTrackProductViewed } from 'lib/segment'
import {
  SEGMENT_CATEGORY_QUERY_ID,
  SEGMENT_JUST_FOR_YOU_QUERY_ID,
  SEGMENT_RECOMMENDATION_QUERY_ID,
  SEGMENT_STORAGE_KEY_IS_REFRESHED,
  SEGMENT_STORAGE_KEY_PRODUCT,
  SEGMENT_STORAGE_KEY_SEARCH_ID,
  SEGMENT_STORAGE_KEY_SEARCH_IDX,
  SEGMENT_STORAGE_KEY_SSR,
} from 'lib/segment/const'
import StateStorage from 'lib/state-storage'
import Tracking from 'lib/tracking'
import { PAGE_NAME } from 'pages/const'
import ErrorDefault from 'components/error/error-default'
import ScrollToTop from 'components/shared/scroll-to-top/ScrollToTop'
import BasePage from '../base'
import styles from './styles.scss'
import { getIsCoachMarkClosed, getSchema, getLocalizedLink } from './utils'

class Product extends BasePage {
  constructor(props) {
    super(props)

    this.state = {
      relatedListActive: false,
      ref_id: null,
      ref_type: null,
    }
  }

  static pageIndex = 0

  static getProductIdFromUrl({ req, query }) {
    const urlId =
      req && req.params && req.params.id ? req.params.id : query.id || 1
    const urlIdTokens = urlId.split('-')
    return urlIdTokens[0]
  }

  static async getInitialProps({
    store,
    query,
    req,
    res,
    isServer,
    user_uid,
    segment_anonymous_id,
    device,
  }) {
    const isVirtualTryOn = query['virtual-try-on'] === 'true'
    Product.pageIndex += 1
    const auth = StateStorage.getCookie('auth')

    if (isServer) {
      BasePage.loadInitialContentFromServer({
        req,
        res,
        store,
        skipAuthorization: true,
      })
    } else {
      BasePage.loadInitialContentFromClient(req, res, store)
    }
    let product = {}

    try {
      product = await Product.getInitialContent(
        store,
        query,
        req,
        isServer,
        user_uid,
        segment_anonymous_id,
        device,
        auth,
      )
    } catch (e) {
      return {
        error: e.body?.error || e.message,
        key: Product.pageIndex,
      }
    }

    return { error: product.error, isVirtualTryOn }
  }

  static PRODUCT_FETCH_RETRY_COUNT = 2

  static PRODUCT_FETCH_RETRY_DELAY_MS = 500

  static isRetryableProductFetchError(error) {
    // A malformed/missing product id is a definitive client-side error, not a
    // transient backend failure, so retrying it would be pointless.
    return error?.body?.error?.code !== API_INVALID_PRODUCT_RES.body.error.code
  }

  static async getProductWithRetry(productOptions) {
    let lastError

    for (
      let attempt = 0;
      attempt <= Product.PRODUCT_FETCH_RETRY_COUNT;
      attempt += 1
    ) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await ProductApiHandler.getProduct(productOptions)
      } catch (error) {
        lastError = error

        if (
          !Product.isRetryableProductFetchError(error) ||
          attempt === Product.PRODUCT_FETCH_RETRY_COUNT
        ) {
          throw error
        }

        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(
            resolve,
            Product.PRODUCT_FETCH_RETRY_DELAY_MS * 2 ** attempt,
          )
        })
      }
    }

    throw lastError
  }

  static async getInitialContent(
    store,
    query,
    req,
    isServer,
    user_uid,
    segment_anonymous_id,
    device,
    auth,
  ) {
    const { lang } = query
    const shop = query.zone || StateStorage.getShop()
    let token = null
    let isGuestMode = StateStorage.isGuestMode()

    if (isServer) {
      token = auth && auth.user ? auth.token : null
      isGuestMode = !(auth && auth.user)
    }

    const productOptions = {
      product_id: Product.getProductIdFromUrl({ req, query }),
      token,
      shop,
      language: lang,
      isGuestMode,
      ...(Boolean(user_uid) && { userUId: user_uid }),
      ...(Boolean(segment_anonymous_id) && {
        segmentAnonymousID: segment_anonymous_id,
      }),
      ...(Boolean(device) && { device }),
    }

    try {
      const product = await Product.getProductWithRetry(productOptions)

      store.dispatch(productDuck.creators.receive(product))
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(
          SEGMENT_CATEGORY_QUERY_ID,
          product.id_recommendation,
        )
      }

      return {
        ...product,
        userUID: user_uid,
        segmentID: segment_anonymous_id,
        device,
      }
    } catch (error) {
      store.dispatch(productDuck.creators.error(error))
      return {
        error: error?.error || error?.body?.error || error?.message || true,
      }
    }
  }

  static async track(product) {
    if (typeof window === 'undefined') {
      return
    }

    const productId = product.id_product || product.id_product_2

    const res = await ExchangeRateApiHandler.convertTo(
      product.prices.discounted_price || product.prices.original_price,
    )

    if (res) {
      Tracking.trackEvent(Tracking.EVENT_NAME_VIEW_CONTENT_PRODUCT, {
        'view-content-product': 'product',
        'view-content-product-id': productId,
        'view-content-product-price-usd': res,
      })
    }
  }

  isProductShortUrl() {
    const productPageShortUrlPattern = /\/[0-9]{3,}\.html/gi
    const isProductShortUrl = productPageShortUrlPattern.test(Router.asPath)
    return isProductShortUrl
  }

  isProductUrl() {
    const productUrlPattern = /\/[-a-z]{3,}\/\d+-/gi
    const isProductUrl = productUrlPattern.test(document.referrer)
    return isProductUrl
  }

  getNonLocalizePath() {
    const categoryPathPattern = /\/[-a-z]{3,}/gi
    const previousCategory = Router.asPath.match(categoryPathPattern)[0]
    const nonLocalizeHref = this.getNormalizedLinks().linkWithShop
    const nonLocalizePath = nonLocalizeHref.replace(
      categoryPathPattern,
      previousCategory,
    )
    return `${nonLocalizePath}`
  }

  getAutoCorrectpathFromShortUrl() {
    const nonLocalizeHref = this.getNormalizedLinks().linkWithShop
    return `${nonLocalizeHref}`
  }

  // eslint-disable-next-line consistent-return
  afterPageMount() {
    const { error, product, products } = this.props

    if (error || !product) {
      return this.renderError()
    }

    document.body.classList.add('pomelo-page-product')
    document.body.setAttribute('rel', product?.id_product || 0)

    const isProductShortUrl = this.isProductShortUrl()
    const isProductUrl = this.isProductUrl()

    let urlReplateStatePath = null
    if (isProductShortUrl) {
      urlReplateStatePath = this.getAutoCorrectpathFromShortUrl()
    } else if (isProductUrl) {
      urlReplateStatePath = this.getNonLocalizePath()
    }

    if (urlReplateStatePath) {
      const { search } = window.location
      window.history.replaceState(null, '', `${urlReplateStatePath}${search}`)
    }

    sessionStorage.setItem(
      SEGMENT_CATEGORY_QUERY_ID,
      products?.id_recommendation,
    )

    if (product) {
      this.trackEvents(this.props)
    }
  }

  fetchProduct = async () => {
    const {
      product,
      internationalization: { shop, lang },
      segmentAnonymousID,
      userUID,
      device,
    } = this.props

    const auth = StateStorage.getCookie('auth')
    const token = auth && auth.user ? auth.token : null
    const isGuestMode = !(auth && auth.user)
    const productOptions = {
      product_id: product?.id_product,
      token,
      shop,
      language: lang,
      isGuestMode,
      ...(Boolean(userUID) && { userUId: userUID }),
      ...(Boolean(segmentAnonymousID) && {
        segmentAnonymousID,
      }),
      ...(Boolean(device) && { device }),
    }

    const newProduct = await ProductApiHandler.getProduct(productOptions)

    this.props.dispatch(productDuck.creators.receive(newProduct))
  }

  componentDidUpdate(prevProps) {
    const { user: prevUser } = prevProps
    const { product, user } = this.props

    if (JSON.stringify(prevUser) !== JSON.stringify(user)) {
      this.fetchProduct()
    }

    if (
      prevProps.product &&
      product &&
      prevProps.product.id_product !== product.id_product
    ) {
      document.body.setAttribute('rel', `${product.id_product || 0}`)
      this.trackEvents(this.props)
      window.scrollTo(0, 0)
    }
  }

  onPageLeave() {
    document.body.classList.remove('pomelo-page-product')
    document.body.removeAttribute('rel')
    sessionStorage.removeItem(SEGMENT_STORAGE_KEY_PRODUCT.type) // remove previous refType on leaving the PDP
  }

  trackEvents({ product }) {
    const click_position = sessionStorage.getItem(
      SEGMENT_STORAGE_KEY_PRODUCT.position,
    )
    const color_swatch = sessionStorage.getItem(
      SEGMENT_STORAGE_KEY_PRODUCT.colorSwatch,
    )
    const ref_id = sessionStorage.getItem(SEGMENT_STORAGE_KEY_PRODUCT.id)
    const ref_type = sessionStorage.getItem(SEGMENT_STORAGE_KEY_PRODUCT.type)

    this.setState({
      ref_id,
      ref_type,
    })

    const sub_position = sessionStorage.getItem(
      SEGMENT_STORAGE_KEY_PRODUCT.subPos,
    )
    const query_id = sessionStorage.getItem(SEGMENT_STORAGE_KEY_SEARCH_ID)
    const query_idx = sessionStorage.getItem(SEGMENT_STORAGE_KEY_SEARCH_IDX)
    const id_lookbook = Number(
      sessionStorage.getItem(SEGMENT_STORAGE_KEY_PRODUCT.id_lookbook),
    )
    const lookbook_name = sessionStorage.getItem(
      SEGMENT_STORAGE_KEY_PRODUCT.lookbook_name,
    )

    sessionStorage.removeItem(SEGMENT_STORAGE_KEY_PRODUCT.position)
    sessionStorage.removeItem(SEGMENT_STORAGE_KEY_PRODUCT.colorSwatch)
    sessionStorage.removeItem(SEGMENT_STORAGE_KEY_PRODUCT.id)
    sessionStorage.removeItem(SEGMENT_STORAGE_KEY_PRODUCT.subPos)
    sessionStorage.removeItem(SEGMENT_STORAGE_KEY_PRODUCT.id_lookbook)
    sessionStorage.removeItem(SEGMENT_STORAGE_KEY_PRODUCT.lookbook_name)
    sessionStorage.removeItem(SEGMENT_STORAGE_KEY_SEARCH_ID)
    sessionStorage.removeItem(SEGMENT_STORAGE_KEY_SEARCH_IDX)
    const isSSRTracked = sessionStorage.getItem(SEGMENT_STORAGE_KEY_SSR)

    // Track for SSR
    if (!ref_type && !isSSRTracked) {
      const urlParams = new URLSearchParams(window.location.search)
      const paramRefId = urlParams.get('ref_id')
      const paramRefType = urlParams.get('ref_type')
      const ref = {
        ...(!!paramRefId && { ref_id: paramRefId }),
        ...(!!paramRefType && { ref_type: paramRefType }),
      }

      if (sessionStorage.getItem(SEGMENT_STORAGE_KEY_IS_REFRESHED)) {
        sessionStorage.removeItem(SEGMENT_STORAGE_KEY_IS_REFRESHED)
        ref.refresh = true
      }

      segmentTrackProductViewed(product, ref)
      sessionStorage.setItem(
        SEGMENT_STORAGE_KEY_PRODUCT.stack,
        JSON.stringify({ [SEGMENT_STORAGE_KEY_PRODUCT.ssr_uuid]: ref }),
      )
    } else if (ref_type) {
      let recommendationIdKey

      switch (ref_type) {
        case 'category':
          recommendationIdKey = SEGMENT_CATEGORY_QUERY_ID
          break
        case 'pdp_complete_look':
          recommendationIdKey = SEGMENT_RECOMMENDATION_QUERY_ID
          break
        case 'pdp_just_for_you':
          recommendationIdKey = SEGMENT_JUST_FOR_YOU_QUERY_ID
          break
        default:
          recommendationIdKey = ''
          break
      }

      const id_recommendation = sessionStorage.getItem(recommendationIdKey)

      segmentTrackProductViewed(product, {
        click_position,
        color_swatch: color_swatch?.toLowerCase() === 'true',
        ref_id,
        ref_type,
        sub_position,
        query_id,
        index: query_idx,
        id_recommendation,
        product_id: product.id_product,
        ...(!!id_lookbook && { id_lookbook }),
        ...(!!lookbook_name && { lookbook_name }),
      })
    }

    sessionStorage.setItem(SEGMENT_STORAGE_KEY_SSR, '1')
    Product.track(product)
  }

  getPageImage() {
    const { product } = this.props
    let image = ''

    if (product) {
      if (product.images && product.images.length) {
        image = product.images[0].full_path
      } else if (product.full_cover_img_v2) {
        image = product.full_cover_img_v2
      }
    }

    return image
  }

  getPageMeta() {
    const { description, product, t } = this.props
    const baseDescription = t(
      'Shop the latest fashion at POMELO fashion. Designed and manufactured in house for quality and style. Dresses, tops, knits, jackets, denim, accessories and more!',
    )

    return {
      title: `${product.name} - Pomelo Fashion`,
      description: description
        ? `${description.replace(/<(?:.|\n)*?>/gm, ' ')} - ${baseDescription}`
        : baseDescription,
      image: this.getPageImage(),
      preloadImage: true,
    }
  }

  getNormalizedLinks() {
    const {
      internationalization: { country, language },
      product,
    } = this.props
    const categoryRewrite = product.category?.link_rewrite || 'clothes'

    let productNameLink = product.link_rewrite || ''
    if (language === 'th') {
      productNameLink = getLocalizedLink(product.name)
    }

    const productNameLinkStr = productNameLink ? `-${productNameLink}` : ''
    const link = `/${categoryRewrite}/${product.id_product}${productNameLinkStr}.html`

    return {
      link,
      linkWithShop: `/${country}/${language}${link}`,
    }
  }

  renderContent() {
    const { product, isPhone, isVirtualTryOn } = this.props
    const normalizedLink = this.getNormalizedLinks()

    const schema = getSchema(product, normalizedLink)
    const refId = this.state.ref_id || product?.id_product
    const refType = this.state.ref_type || 'external'

    return (
      <CommonLayout
        customCleanedPath={normalizedLink.link}
        customOriginPath={normalizedLink.linkWithShop}
        meta={this.getPageMeta()}
        showUsp={!isPhone}
        noSidePadding
        pageId={product ? product.id_product || product.id_product_2 : 0}
        pageName={PAGE_NAME.pdp}
        schema={schema}
      >
        <ScrollToTop />
        <style jsx>{styles}</style>
        <ProductContainer
          refId={refId}
          refType={refType}
          isVirtualTryOn={isVirtualTryOn}
          product={product}
          link={normalizedLink.linkWithShop}
          isCoachMarkClosed={getIsCoachMarkClosed('pdp_size_coach_mark')}
          pageId={product ? product.id_product || product.id_product_2 : 0}
          pageName="product"
        />
      </CommonLayout>
    )
  }

  renderError() {
    const { isPhone } = this.props

    return (
      <CommonLayout showUsp={!isPhone}>
        <ScrollToTop />
        <style jsx>{styles}</style>
        <ErrorDefault showDefaultError={false} showPDPError showSearch />
      </CommonLayout>
    )
  }

  render() {
    // Note: the product data here come from redux store not from getInitialProps
    // In getInitialContent we are actually setting the product redux state from the server side
    const { error, product } = this.props

    if (error || !product) {
      return this.renderError()
    }

    return this.renderContent()
  }
}

Product.propTypes = {
  isPhone: PropTypes.bool,
  t: PropTypes.func,
  token: PropTypes.string,
  internationalization: PropTypes.shape({}),
  user: PropTypes.shape({}),
  userUID: PropTypes.string,
  segmentID: PropTypes.string,
  device: PropTypes.string,
  products: PropTypes.shape({ id_recommendation: PropTypes.string }),
  isVirtualTryOn: PropTypes.bool,
}

Product.defaultProps = {
  products: undefined,
  isVirtualTryOn: false,
}

const Extended = withI18next()(Product)

export default connect((state) => ({
  isPhone: state.device.isPhone,
  loading: state.product.isFetching,
  product: state.product.payload,
  products: state.products.payload,
  token: state.auth.token,
  internationalization: state.internationalization,
  user: state.auth.user,
}))(Extended)
